import { useState, useMemo } from 'react'
import { API_ORIGIN } from '../config'

const JWT_KEY = 'gssnhr_auth_token'

interface AuthUser {
    email: string
    name: string
}

interface AuthState {
    isAuthenticated: boolean
    user: AuthUser | null
    token: string | null
    login: (token: string) => void
    logout: () => void
    register: (name: string, email: string, password: string) => Promise<void>
    loginWithCredentials: (email: string, password: string) => Promise<void>
}

function decodeUser(token: string): AuthUser | null {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        if (payload.exp * 1000 < Date.now()) return null
        return { email: payload.sub, name: payload.name }
    } catch {
        return null
    }
}

export function useAuth(): AuthState {
    const [token, setToken] = useState<string | null>(() => {
        const stored = localStorage.getItem(JWT_KEY)
        if (stored && decodeUser(stored)) return stored
        localStorage.removeItem(JWT_KEY)
        return null
    })

    const user = useMemo(() => (token ? decodeUser(token) : null), [token])

    const login = (newToken: string) => {
        localStorage.setItem(JWT_KEY, newToken)
        setToken(newToken)
    }

    const logout = () => {
        localStorage.removeItem(JWT_KEY)
        setToken(null)
    }

    const register = async (name: string, email: string, password: string) => {
        const res = await fetch(`${API_ORIGIN}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email, password }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Registration failed.')
        login(data.token)
    }

    const loginWithCredentials = async (email: string, password: string) => {
        const res = await fetch(`${API_ORIGIN}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error || 'Login failed.')
        login(data.token)
    }

    return {
        isAuthenticated: !!user,
        user,
        token,
        login,
        logout,
        register,
        loginWithCredentials,
    }
}
